import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import { createPopper } from "@popperjs/core";

export default class extends Controller {
  static values = {
    submitUrl: String,
    appliedEventName: String,
    targetId: String,
    ownerSelector: String,
  };

  static targets = ["dropdown", "trigger", "input", "spinner", "submit"];

  connect() {
    if (this.hasTriggerTarget && this.hasDropdownTarget) {
      this.popper = createPopper(this.triggerTarget, this.dropdownTarget, {
        placement: "bottom-end",
        modifiers: [
          { name: "offset", options: { offset: [0, 6] } },
          { name: "preventOverflow", options: { padding: 8 } },
          { name: "flip", options: { padding: 8 } },
        ],
      });
    }

    this.boundHandleApplied = this.handleApplied.bind(this);
    if (this.appliedEventNameValue) {
      window.addEventListener(
        this.appliedEventNameValue,
        this.boundHandleApplied,
      );
    }

    this.boundHandleEsc = this.handleEsc.bind(this);
    this.boundHandleOutside = this.handleOutsideClick.bind(this);

    // Listen on the owning .c-fault-search (an ancestor) so only this bar's
    // focus event reaches us — avoids target_id filtering.
    this.boundHandleSearchFocused = this.handleSearchFocused.bind(this);
    this.searchOwner = this.element.closest(this.ownerSelectorValue || "");
    if (this.searchOwner) {
      this.searchOwner.addEventListener(
        "fault-search:focused",
        this.boundHandleSearchFocused,
      );
    }
  }

  disconnect() {
    if (this.appliedEventNameValue) {
      window.removeEventListener(
        this.appliedEventNameValue,
        this.boundHandleApplied,
      );
    }
    document.removeEventListener("keydown", this.boundHandleEsc);
    document.removeEventListener("click", this.boundHandleOutside);
    if (this.searchOwner) {
      this.searchOwner.removeEventListener(
        "fault-search:focused",
        this.boundHandleSearchFocused,
      );
    }
    this.popper?.destroy();
  }

  toggle(event) {
    event.preventDefault();
    if (
      this.hasDropdownTarget &&
      this.dropdownTarget.classList.contains("open")
    ) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("open");
    }
    // Popper can't measure a display:none element, so update after .open.
    this.popper?.update();

    document.addEventListener("keydown", this.boundHandleEsc);
    document.addEventListener("click", this.boundHandleOutside);

    this.element.dispatchEvent(
      new CustomEvent("ai-search:opened", { bubbles: true }),
    );

    if (this.hasInputTarget) {
      this.inputTarget.focus();
    }
  }

  close() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove("open");
    }
    document.removeEventListener("keydown", this.boundHandleEsc);
    document.removeEventListener("click", this.boundHandleOutside);
    this.element.dispatchEvent(
      new CustomEvent("ai-search:closed", { bubbles: true }),
    );
  }

  fillSuggestion(event) {
    event.preventDefault();
    const text = event.currentTarget.dataset.suggestion || "";
    if (this.hasInputTarget) {
      this.inputTarget.value = text;
      this.inputTarget.focus();
    }
  }

  translate(event) {
    event.preventDefault();
    this.submit();
  }

  async submit() {
    if (this.submitting) return;

    const description = this.hasInputTarget
      ? this.inputTarget.value.trim()
      : "";
    if (!description) return;

    this.submitting = true;
    this.setLoading(true);

    try {
      const formData = new FormData();
      formData.append("description", description);
      formData.append("target_id", this.targetIdValue);
      formData.append("current_query", this.readCurrentQuery());

      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: formData,
        credentials: "same-origin",
      });

      const text = await response.text();
      if (text && text.trim().length) {
        Turbo.renderStreamMessage(text);
      } else if (!response.ok) {
        this.renderError(`Request failed (${response.status}).`);
      }
    } catch (e) {
      this.renderError("Network error. Try again in a moment.");
    } finally {
      this.submitting = false;
      this.setLoading(false);
    }
  }

  handleApplied(event) {
    if (!event.detail) return;
    if (
      event.detail.target_id &&
      event.detail.target_id !== this.targetIdValue
    ) {
      return;
    }
    this.close();
  }

  handleEsc(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }

  handleSearchFocused() {
    this.close();
  }

  setLoading(loading) {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("d-none", !loading);
    }
    if (this.hasSubmitTarget) {
      this.submitTargets.forEach((b) => {
        b.disabled = loading;
      });
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = loading;
    }
  }

  readCurrentQuery() {
    if (!this.ownerSelectorValue) return "";
    const owner = this.element.closest(this.ownerSelectorValue);
    if (!owner) return "";
    const input = owner.querySelector("input.search-box, textarea.search-box");
    return input ? input.value || "" : "";
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : "";
  }

  renderError(message) {
    const body = document.getElementById(this.targetIdValue);
    if (!body) return;
    body.innerHTML = '<div class="ai-search-error" role="alert"><p></p></div>';
    body.querySelector("p").textContent = message;
  }
}
